// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// Wraps an admin route so it only renders if the signed-in admin has the
// required permission. Owners and master-key sessions always pass.
//
// Use cases:
//   - User types an admin URL directly (e.g. /admin/dashboard/firm-settings)
//     → without this gate the page would mount and start firing API calls
//       that all 403 — ugly. With this gate, they get a clean "no access"
//       message and a link back to the dashboard.
//   - You disable a permission for a user while they're logged in → next
//     navigation through this gate kicks them off the protected page.
//
// Notes:
//   - This is a UX layer, NOT a security barrier. The backend enforces
//     permissions via middleware regardless of whether this wrapper is used.
//   - Permissions are loaded once via getMyPermissions(); we cache them in
//     the wrapper itself (via context, naturally) so adding it to each route
//     doesn't trigger N fetches.

import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminApi } from '../utils/adminApi';

// requirement is one of:
//   'owner'   — owner role or master-key session
//   '<key>'   — a specific permission_key from PERMISSIONS list
export default function RequirePermission({ requirement, children }) {
  const { adminUser, loading: authLoading } = useAdminAuth();
  const [perms, setPerms] = useState(null);
  const [permsLoading, setPermsLoading] = useState(true);

  useEffect(() => {
    if (!adminUser) return;
    let cancelled = false;
    adminApi.getMyPermissions()
      .then(data => { if (!cancelled) { setPerms(data); setPermsLoading(false); } })
      .catch(() => {
        if (!cancelled) {
          setPerms({ role: adminUser.role, permissions: {}, isMasterKey: false });
          setPermsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [adminUser]);

  // Still resolving auth or perms — render a minimal loader
  if (authLoading || permsLoading) {
    return (
      <div style={{ padding: 32 }}>
        <div className="spinner" style={{ borderTopColor: '#0F1838', width: 20, height: 20 }} />
      </div>
    );
  }

  if (!adminUser) return <Navigate to="/admin" replace />;

  const isOwnerEquivalent = perms?.role === 'owner' || perms?.isMasterKey;

  // Pass conditions
  if (isOwnerEquivalent) return children;
  if (requirement === 'owner') {
    // Non-owner needs to see the access-denied page
    return <AccessDenied requirement="owner" />;
  }
  if (perms?.permissions?.[requirement]) return children;

  return <AccessDenied requirement={requirement} />;
}

function AccessDenied({ requirement }) {
  const label = requirement === 'owner'
    ? 'This page is owner-only.'
    : `This page requires the "${requirement}" permission, which isn't enabled for your account.`;

  return (
    <div style={{ maxWidth: 540, margin: '60px auto', padding: '32px 24px', textAlign: 'center' }}>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 22, marginBottom: 8 }}>Access Denied</h1>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, color: '#555', lineHeight: 1.6, marginBottom: 24 }}>
        {label} If you think this is a mistake, ask the firm owner to update your permissions in Staff Management.
      </p>
      <Link to="/admin/dashboard" className="btn btn-primary">← Back to Dashboard</Link>
    </div>
  );
}
